import { useCallback, useState } from "react";

/**
 * Custom hook to manage expansion state of TimelineItem
 *
 * Supports both controlled and uncontrolled modes.
 * In controlled mode, the expanded state is managed externally.
 * In uncontrolled mode, the state is managed internally.
 *
 * @param defaultExpanded - Initial expanded state for uncontrolled mode
 * @param expanded - External expanded state for controlled mode
 * @returns Object containing expansion state and toggle handler
 */
export const useTimelineItemExpansion = (
  defaultExpanded: boolean = false,
  expanded?: boolean,
) => {
  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled expanded prop if provided, otherwise use internal state
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;

  // Toggle expansion handler
  const toggleExpansion = useCallback(() => {
    if (expanded === undefined) {
      // Uncontrolled mode - manage internal state
      setInternalExpanded((prev) => !prev);
    }
    // Note: In controlled mode, we don't update state directly
    // The parent component is responsible for updating the expanded prop
  }, [expanded]);

  return {
    isExpanded,
    toggleExpansion,
  };
};
